import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';

const DISMISS_KEY = 'apexflow.feed-stale-banner.dismissed_at';
const REDISMISS_AFTER_MIN = 30;
const POLL_INTERVAL_MS = 60_000;

function isDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageMin = (Date.now() - Number(ts)) / 60_000;
    return ageMin < REDISMISS_AFTER_MIN;
  } catch (_) { return false; }
}

/**
 * FeedStaleBanner — global banner that surfaces when the market-data
 * feed degrades. Polls /api/system/telemetry every 60s and shows when:
 *   • price_poll_ok = false (CoinGecko polling stopped)
 *   • binance_ws    = false (Binance websocket disconnected)
 *
 * Designed as a "FEED STALE" pre-emptive warning so a future feed
 * outage surfaces as one visible banner instead of multiple "Failed"
 * strings under each chart panel. Dismissable for 30 min via local
 * storage. Paper-mode posture unchanged.
 */
export default function FeedStaleBanner() {
  const [tel, setTel] = useState(null);
  const [hidden, setHidden] = useState(isDismissedRecently);

  const probe = useCallback(async () => {
    try {
      const { data } = await api.get('/system/telemetry');
      setTel(data);
    } catch (_) { /* unauth/network — silent */ }
  }, []);

  useEffect(() => {
    probe();
    const t = setInterval(probe, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [probe]);

  if (hidden || !tel) return null;
  // The banner should fire ONLY when the system has no working
  // market-data source. CoinGecko polling failing alone is fine —
  // background_loops falls back to Binance automatically. So we only
  // alarm when (a) Binance WS is down AND CoinGecko poll is failing,
  // OR (b) zero symbols are being tracked (everything starved).
  const cgDown = tel.price_poll_ok === false;
  const wsDown = tel.binance_ws === false;
  const noSymbols = (tel.symbols_tracked ?? 1) === 0;
  const allSourcesDown = (cgDown && wsDown) || noSymbols;
  if (!allSourcesDown) return null;

  const reasons = [
    cgDown && 'CoinGecko polling stopped',
    wsDown && 'Binance WebSocket disconnected',
    noSymbols && 'no symbols being tracked',
  ].filter(Boolean).join(' · ');

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch (_) { /* quota */ }
    setHidden(true);
  };

  return (
    <div data-testid="feed-stale-banner"
         role="alert"
         style={{
           position: 'sticky', top: 0, zIndex: 60,
           display: 'flex', alignItems: 'center', gap: 12,
           padding: '8px 16px',
           background: 'rgba(255, 59, 48, 0.10)',
           borderBottom: '1px solid var(--accent-red, #FF3B30)',
           color: 'var(--accent-red, #FF3B30)',
           fontSize: 11, letterSpacing: '0.08em',
           fontFamily: 'JetBrains Mono, monospace',
         }}>
      <AlertTriangle size={14} />
      <span style={{ fontWeight: 700, letterSpacing: '0.18em' }}>
        FEED STALE
      </span>
      <span style={{ flex: 1, color: 'var(--text, #fff)' }}>
        Market-data feed degraded — {reasons}. Charts and intel
        endpoints may show empty or stale values until the feed
        recovers. Paper-mode unaffected.
      </span>
      <a href="/analytics"
         data-testid="feed-stale-banner-link"
         style={{
           display: 'inline-flex', alignItems: 'center', gap: 4,
           color: 'var(--accent-amber, #FFB000)',
           textDecoration: 'none',
           border: '1px solid var(--accent-amber, #FFB000)',
           padding: '3px 10px',
         }}>
        OPEN ANALYTICS <ExternalLink size={11} />
      </a>
      <button onClick={dismiss}
              data-testid="feed-stale-banner-dismiss"
              aria-label="Dismiss for 30 minutes"
              title="Dismiss for 30 minutes"
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--text-muted, #71717A)',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}>
        <X size={14} />
      </button>
    </div>
  );
}
